/**
 * PageBreadcrumb Component
 * Standardized breadcrumb navigation for all pages
 *
 * @version 2025.1.0
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';

export interface BreadcrumbItem {
  /** Label to display */
  label: string;
  /** Path to navigate to (optional - if not provided, item is not clickable) */
  path?: string;
}

export interface PageBreadcrumbProps {
  /** Array of breadcrumb items. Last item is always the current page (no link) */
  items: BreadcrumbItem[];
  /** Whether to show home icon on first item. Default: true */
  showHomeIcon?: boolean;
  /** Custom margin bottom. Default: 2 */
  mb?: number;
}

/**
 * PageBreadcrumb - Standardized breadcrumb component
 *
 * @example
 * // Simple usage
 * <PageBreadcrumb items={[
 *   { label: 'Início', path: '/' },
 *   { label: 'Empresas' }
 * ]} />
 *
 * @example
 * // With intermediate navigation
 * <PageBreadcrumb items={[
 *   { label: 'Início', path: '/' },
 *   { label: 'Medicina', path: '/medicina' },
 *   { label: 'Recepção' }
 * ]} />
 */
export const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({
  items,
  showHomeIcon = true,
  mb = 2,
}) => {
  const navigate = useNavigate();

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb }}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isFirst = index === 0;
        const showIcon = isFirst && showHomeIcon;

        // Last item is always text (current page) - larger and bolder
        if (isLast) {
          return (
            <Typography key={index} color="text.primary" fontWeight={500}>
              {item.label}
            </Typography>
          );
        }

        // Items with path are clickable - smaller font
        if (item.path) {
          return (
            <Link
              key={index}
              component="button"
              variant="body2"
              color="text.secondary"
              underline="hover"
              onClick={() => navigate(item.path!)}
              sx={{ cursor: 'pointer', fontSize: '0.8125rem' }}
            >
              {showIcon && (
                <HomeIcon sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5, mt: '-2px' }} />
              )}
              {item.label}
            </Link>
          );
        }

        // Items without path are just text - smaller font
        return (
          <Typography
            key={index}
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '0.8125rem' }}
          >
            {showIcon && (
              <HomeIcon sx={{ fontSize: '0.875rem', verticalAlign: 'middle', mr: 0.5, mt: '-2px' }} />
            )}
            {item.label}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
};

export default PageBreadcrumb;
